import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, StatusPill, Loader } from "@/components/ui-bits";
import { ArrowDownToLine, Loader2, CalendarClock, Wallet, Copy, Check, Save } from "lucide-react";

export default function Withdraw() {
  const [d, setD] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [addr, setAddr] = useState("");
  const [savingAddr, setSavingAddr] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => API.get("/withdrawals").then((r) => { setD(r.data); setAddr(r.data.bep20_address || ""); });
  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const amt = d.exact_amount != null ? d.exact_amount : parseFloat(amount);
      const r = await API.post("/withdraw", { amount: amt });
      toast.success(r.data.message); setAmount(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveAddr = async () => {
    setSavingAddr(true);
    try {
      const r = await API.post("/withdraw/bep20-address", { address: addr });
      toast.success(r.data.message);
      setD((p) => ({ ...p, bep20_address: r.data.bep20_address }));
    } catch (e) { toast.error(apiError(e)); } finally { setSavingAddr(false); }
  };

  const copyAddr = async () => {
    if (!d?.bep20_address) return;
    try { await navigator.clipboard.writeText(d.bep20_address); } catch { /* ignore */ }
    setCopied(true); toast.success("Address copied");
    setTimeout(() => setCopied(false), 1500);
  };

  if (!d) return <Loader label="Loading withdrawals" />;
  const fixed = d.exact_amount != null;
  const amt = fixed ? d.exact_amount : (parseFloat(amount) || 0);
  const fee = amt * d.fee_percent / 100;
  const net = amt; // fee is charged on top; user receives the full amount
  const totalDeducted = amt + fee;
  const canSubmit = d.eligible && d.is_withdrawal_day && (fixed || parseFloat(amount) > 0);

  const tierLabel = d.withdrawal_tier === 1 ? "1st withdrawal" : d.withdrawal_tier === 2 ? "2nd withdrawal" : "3rd+ withdrawal";

  return (
    <div>
      <PageHeader title="Withdrawals" subtitle={`Processed on ${d.withdrawal_days.join(" & ")}. A ${d.fee_percent}% platform fee applies and must be available above the amount.`} testid="withdraw-header" />

      <div className={`glass rounded-2xl p-4 mb-6 flex items-center gap-3 text-sm ${d.is_withdrawal_day ? "text-emerald-300" : "text-amber-300"}`}>
        <CalendarClock className="h-5 w-5" />
        Today is {d.today} — {d.is_withdrawal_day ? "withdrawals are OPEN." : `withdrawals are CLOSED. Available on ${d.withdrawal_days.join(" & ")}.`}
      </div>

      {/* BEP20 payout address */}
      <div className="glass rounded-2xl p-6 mb-6" data-testid="bep20-address-card">
        <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-1">
          <Wallet className="h-5 w-5 text-amber-400" /> BEP20 Wallet Address
        </h3>
        <p className="text-xs text-slate-500 mb-4">Your USDT (BEP20) wallet address for receiving payouts. You can update it anytime.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input data-testid="bep20-address-input" value={addr} onChange={(e) => setAddr(e.target.value)}
            placeholder="0x... (BEP20 / BSC address)"
            className="flex-1 rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white outline-none focus:border-amber-500/50 font-num text-sm" />
          <button data-testid="bep20-save-btn" onClick={saveAddr} disabled={savingAddr}
            className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-slate-900 font-semibold px-5 py-3 rounded-xl">
            {savingAddr ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
        {d.bep20_address && (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3">
            <span className="font-num text-sm text-emerald-300 break-all" data-testid="bep20-address-value">{d.bep20_address}</span>
            <button data-testid="bep20-copy-btn" onClick={copyAddr}
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 hover:bg-cyan-500/25">
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-display text-lg font-semibold text-white mb-1">Request Withdrawal</h3>
          <p className="text-xs text-slate-500 mb-4" data-testid="withdraw-tier">Stage: <span className="text-emerald-300 font-semibold">{tierLabel}</span></p>

          {!d.eligible && (
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs p-3 mb-4" data-testid="withdraw-ineligible">
              {d.eligibility_reason}
            </div>
          )}

          <div className="space-y-3 text-sm">
            <Row label="Available balance" value={fmt(d.available_balance)} />
            {fixed
              ? <Row label="Fixed amount (once)" value={<span className="text-emerald-300 font-bold">{fmt(d.exact_amount)}</span>} />
              : <Row label="Minimum amount" value={fmt(d.min_amount)} />}
            {d.withdrawal_multiple && <Row label="Must be multiple of" value={fmt(d.withdrawal_multiple)} />}
          </div>

          {fixed ? (
            <div data-testid="withdraw-fixed-amount" className="mt-4 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white font-num flex items-center justify-between">
              <span>{fmt(d.exact_amount)}</span>
              <span className="text-[0.65rem] uppercase tracking-wide text-slate-500">fixed</span>
            </div>
          ) : (
            <input data-testid="withdraw-amount" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-4 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white outline-none focus:border-emerald-500/50" placeholder={`Multiple of $${d.withdrawal_multiple || d.min_amount}`} />
          )}
          {amt > 0 && (
            <div className="mt-4 rounded-xl bg-white/5 p-4 space-y-2 text-sm">
              <Row label="You receive" value={<span className="text-emerald-300 font-bold">{fmt(net)}</span>} />
              <Row label={`Fee (${d.fee_percent}%, charged on top)`} value={<span className="text-rose-400">+{fmt(fee)}</span>} />
              <div className="border-t border-white/10 pt-2"><Row label="Total deducted from Main" value={<span className="text-white font-bold">{fmt(totalDeducted)}</span>} /></div>
            </div>
          )}
          <button data-testid="withdraw-submit" disabled={busy || !canSubmit} onClick={submit}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 disabled:cursor-not-allowed text-slate-900 font-semibold py-3 rounded-xl">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} <ArrowDownToLine className="h-4 w-4" /> Request Withdrawal
          </button>
        </div>

        <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="withdrawal-history">
          <h3 className="font-display text-lg font-semibold text-white mb-4">History</h3>
          {d.withdrawals.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No withdrawals yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-white/10">
                  <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">Fee</th><th className="py-2 pr-4">Net</th><th className="py-2 text-right">Status</th>
                </tr></thead>
                <tbody>
                  {d.withdrawals.map((w) => (
                    <tr key={w.id} className="border-b border-white/5 last:border-0">
                      <td className="py-3 pr-4 font-num text-slate-400">{new Date(w.created_at).toLocaleDateString()}</td>
                      <td className="py-3 pr-4 font-num text-white">{fmt(w.amount)}</td>
                      <td className="py-3 pr-4 font-num text-rose-400">{fmt(w.fee)}</td>
                      <td className="py-3 pr-4 font-num text-emerald-300">{fmt(w.net)}</td>
                      <td className="py-3 text-right"><StatusPill status={w.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between"><span className="text-slate-400">{label}</span><span className="font-num text-white">{value}</span></div>
);
